import { useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import {
  AppShell as UiAppShell,
  Button,
  Input,
  SimpleTooltip,
  type AdaptedNavItem,
  type SidebarBadge,
} from '@command-center/ui-system';
import { useDesignSystem } from '@command-center/oneup-modern-ui';
import { computeQueueCounts } from '@oneup/shared';
import { tasksApi } from '@/lib/api';
import { AlicePanel, AliceProvider } from '@/components/alice';
import { CommandPalette } from '@/components/command-palette';
import { CreateRecordDrawer } from '@/components/create-record-drawer';
import { EmailComposerModal } from '@/components/email-composer-modal';
import { GlobalNotice } from '@/components/global-notice';
import { DemoModeBadge } from '@/components/demo/DemoModeBadge';
import { GlobalShortcuts } from '@/components/global-shortcuts';
import { useUiStore } from '@/stores/ui';
import { getBranding } from '@/platform/branding';
import { adminConfig } from '@/platform/config';
import { getNavigationGroups } from '@/platform/navigation';
import { getRouteTitle } from '@/platform/modules';
import { ModernAppShellAdapter } from './ModernAppShellAdapter';

export function AppShell({ children }: { children: ReactNode }) {
  const { isModern } = useDesignSystem();

  return (
    <AliceProvider config={adminConfig.alice} portalKey={adminConfig.appId}>
      {isModern ? (
        <ModernAppShellAdapter>{children}</ModernAppShellAdapter>
      ) : (
        <LegacyShellContent>{children}</LegacyShellContent>
      )}
    </AliceProvider>
  );
}

function LegacyShellContent({ children }: { children: ReactNode }) {
  const branding = useMemo(() => getBranding(), []);
  const groups = useMemo(() => getNavigationGroups(), []);
  const { pathname } = useLocation();
  const head = getRouteTitle(pathname);

  const emailComposerOpen = useUiStore((state) => state.emailComposerOpen);
  const emailComposerInitial = useUiStore((state) => state.emailComposerInitial);
  const setEmailComposerOpen = useUiStore((state) => state.setEmailComposerOpen);
  const openCreateDrawer = useUiStore((state) => state.openCreateDrawer);
  const openCommandPalette = useUiStore((state) => state.openCommandPalette);

  // Same query key as TasksView so the cache is shared — no double-fetch.
  // Sidebar badge consumes counts.today; TasksView is the writer.
  const { data } = useQuery({
    queryKey: ['tasks', 'all'],
    queryFn: () => tasksApi.list({ limit: 300 }),
  });
  const counts = useMemo(() => computeQueueCounts(data?.items ?? []), [data?.items]);

  const decorate = (item: AdaptedNavItem): SidebarBadge | undefined =>
    item.to === '/tasks' && counts.today ? { badge: counts.today } : undefined;

  return (
    <UiAppShell
      branding={branding}
      groups={groups}
      head={head}
      decorate={decorate}
      collapsibleNavGroups={adminConfig.collapsibleNavigationGroups}
      navGroupsInitiallyCollapsed
      navGroupStorageKey={`${adminConfig.appId}:nav-groups`}
      collapsibleSidebar
      sidebarStorageKey={`${adminConfig.appId}:sidebar`}
      rightSidebar={<AlicePanel />}
      searchSlot={
        <div className="relative hidden sm:block">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="text"
            readOnly
            placeholder="Search & Command"
            className="h-8 w-[280px] pl-8 pr-7 text-[13px] lg:w-[340px]"
            aria-label="Open command palette"
            onClick={() => openCommandPalette()}
            onFocus={(event) => {
              event.currentTarget.blur();
              openCommandPalette();
            }}
          />
        </div>
      }
      actions={
        <SimpleTooltip text="Quick Capture · C">
          <Button
            size="sm"
            className="h-9 gap-1.5 px-3"
            onClick={() => openCreateDrawer({ type: 'task' })}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            <span className="hidden sm:inline">Quick Capture</span>
            <span className="sm:hidden">Capture</span>
          </Button>
        </SimpleTooltip>
      }
      overlays={
        <>
          <GlobalShortcuts />
          <CommandPalette />
          <CreateRecordDrawer />
          <EmailComposerModal
            open={emailComposerOpen}
            initial={emailComposerInitial}
            onOpenChange={setEmailComposerOpen}
          />
          <GlobalNotice />
          <DemoModeBadge />
        </>
      }
    >
      {children}
    </UiAppShell>
  );
}
